import { type LabelComponentType, getLabelComponent } from "../../../common-deprecated/components/label/Label";
import {
    type GetLabelType,
    type GetOptionalLabelType,
    type UseLabelType,
    type UseOptionalLabelType,
    getLabelFn,
    getOptionalLabelFn,
    getUseLabel,
    getUseOptionalLabel,
} from "../../../common-deprecated/hooks/useLabel";
import type { UscStateType } from "../redux/store";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";

// ----------------------------------------------------------------------
// Accessibility labels
// ----------------------------------------------------------------------
const a11yLabels = {
    uscA11yButtonSlideNext: "Next slide",
    uscA11yButtonSlidePrevious: "Previous slide",
    uscA11yButtonTaxLocationUpdate: "Update your tax location",
    uscA11yButtonTaxLocationCurrent: "Current tax location",
    uscA11yLabelTaxLocationSearch: "Search tax location",
    uscA11yBreadcrumbsTitle: "You are here",
    uscA11yButtonInsuranceQuoteRemove: "Remove insurance quote",
    uscA11yButtonNext: "Next",
    uscA11yButtonPrevious: "Previous",
    uscA11yInputWidth: "Width",
    uscA11yInputHeight: "Height",
    uscA11yInputLength: "Length",
    uscA11yButtonSaveCar: "Save car",
    uscA11yTabServices: "Services",
    uscA11yMenuHorizontalTitle: "Car details",
    uscA11yFooter: "Similar cars and disclaimers",
    uscA11yContainer: "Car detail page",
    uscA11yButtonClose: "Close",
    uscA11yButtonFullScreen: "View fullscreen",
    uscA11ySlide: "Slide",
};

export const uscLabels = {
    ...a11yLabels,

    // ----------------------------------------------------------------------
    // General
    // ----------------------------------------------------------------------

    uscDealerAddress: "{line1}\n{city} {zip}, {region}", // used in pdp & basket, placeholders: line1, line2, city, zip, region, province, country
    uscDealerGetDirectionsLink: "Get directions in Google Maps",
    uscShowMore: "Show more",
    uscShowLess: "Show less",
    uscDefaultMonthlyRateFormat: "{price} /month",
    uscLoading: "Loading", // General loading label
    uscPromoFromUntilDate: "Valid from {from} until {until}", // Promo date range
    uscPromoUntilDate: "Valid until {until}", // No start date
    uscPromoFromDate: "Valid from {from}", // No end date

    // Disclaimers
    uscPageDisclaimerTitle: "Disclaimers",
    uscPageDisclaimerUsedCash: "",
    uscPageDisclaimerUsedMonthly: "",
    uscPageDisclaimerStockCash: "",
    uscPageDisclaimerStockMonthly: "",
    uscPdpUsedCarouselDisclaimer: "",
    uscPdpStockCarouselDisclaimer: "",

    // TDG SCL Ecolabel disclaimers
    uscEcolabelDisclaimer: "",
    uscEcolabelCO2EmissionDisclaimer: "",
    uscEcolabelFutureCO2PricingDisclaimer: "",
    uscEcolabelElectricTaxExemptionDisclaimer: "",

    uscBasketDisclaimerUsedCash: "",
    uscBasketDisclaimerUsedMonthly: "",
    uscBasketDisclaimerStockCash: "",
    uscBasketDisclaimerStockMonthly: "",
    uscBasketDisclaimerReclaimableVAT: "",
    uscBasketHubDisclaimer:
        "This {model} is currently in stock. However, it is not located at the dealer’s showroom yet. Please contact the dealer to make an appointment or to book a test drive.",
    // These are used in the basket as well as the extras
    uscExtrasDisclaimerWarranty: "",
    uscExtrasDisclaimerCarDelivery: "",

    // Pdf
    uscPdfPriceCash: "Cash",
    uscPdfPriceFinance: "Finance",
    uscPdfPriceInsurance: "Insurance",
    uscPdfDisclaimersTitle: "Disclaimers",
    uscPdfDisclaimer: "",
    uscPdfQRCode: "Your Code",
    uscPdfBasketDetails: "Basket details",
    uscPdfDealerTitle: "Dealer",
    uscPdfDealerContactDetailsTitle: "Contact Details",
    uscPdfDealerAddressTitle: "Address",

    // Mobile Navigation
    uscMobileNavCta: "I'm interested",
    uscMobileNavCtaModalTitle: "Interested in this car?",
    uscMobileNavCtaModalDescription: "",
    uscMobileNavCtaModalMoreInformationTitle: "Want more information",
    uscDiscoverYourCar: "Discover your car",

    // CTA's used on multiple places
    uscBuyThisCarCtaCash: "Buy online with cash",
    uscBuyThisCarCtaMonthly: "Buy online with finance",
    uscReserveThisCarCtaCash: "Reserve this car with cash",
    uscReserveThisCarCtaMonthly: "Reserve this car",
    uscLocalReserveThisCarCtaCash: "Reserve this car with cash",
    uscLocalReserveThisCarCtaMonthly: "Reserve this car",
    uscContactDealerCta: "Contact dealer",
    uscCallDealerCta: "Call dealer",
    uscContactDealerModalTitle: "Information request",

    // Breadcrumbs
    uscBreadcrumbsUsed: "Used cars",
    uscBreadcrumbsStock: "Stock cars",
    uscBreadcrumbsExtras: "Configuration",

    // Taxes and Costs
    uscTaxesAndCostsTitle: "Taxes",
    uscTaxesAndCostsDepartment: "Department",
    uscTaxesAndCostsSelectRegion: "Select region",

    // Calculator error modal
    uscCalculatorErrorTitle: "This is taking too long.",
    uscCalculatorErrorDescription: "Something is wrong. Try refreshing the page or try again later.",
    uscCalculatorErrorButton: "Try again",

    // ⚠️ A temporary label is added for a smoother rollout, so the NMSC can have a different monthlyPriceFormat between v1 and v2
    // The v2 overwrite will only overwrite the config when configured.
    // This should be removed once the migration is finished
    uscV2MonthlyPriceFormat: "", // [price]Disclaimers[/price]

    // ----------------------------------------------------------------------
    // PDP
    // ----------------------------------------------------------------------

    // General
    uscPdpDownloadDocument: "Download",

    // Horizontal menu
    uscPdpMenuHighlights: "Highlights",
    uscPdpMenuServices: "Services",
    uscPdpMenuAccessories: "Accessories",
    uscPdpMenuCarDetails: "Car Details",
    uscPdpMenuDealer: "Dealer",

    // Specs - general
    uscPdpSpecTitle: "Specifications",
    uscPdpSpecEcoImageDescription: "",
    uscPdpSpecEcoImageDisclaimer: "",

    // Specs - Tyre info
    uscPdpTyreInfo: "Tyre info",
    uscPdpTyreInfoDescription: "",
    uscPdpTyreInfoFallback: "Detail page",
    uscPdpTyreInfoFallbackDescription: "This image could not be loaded. To see more information go to:",
    uscPdpTyreInfoNoEprelFallback: "For further information please contact your local retailer.",

    // Specs - Weighted spec titles overwrites
    uscPdpWeightedCombinedCo2: "CO2 combined - Weighted",
    uscPdpWeightedCo2NedcCombined: "CO2 Nedc - Weighted",
    uscPdpWeightedCo2WltpCombined: "CO2 WLTP - Weighted",
    uscPdpWeightedFcNedcCombined: "Fuel consumption Nedc combined - Weighted",
    uscPdpWeightedFcWltpCombined: "Fuel consumption WLTP combined - Weighted",
    uscPdpWeightedFcCsCombined: "Fuel consumption charge sustaining - Weighted",
    uscPdpWeightedEcCombined: "Electric consumption combined - Weighted",
    uscPdpWeightedEfficiencyClassText: "Efficiency class - Weighted",
    uscPdpWeightedEcolabelEcCombined: "Electric consumption in all-electric mode combined - Weighted",

    // Specs - TDG Ecolabel
    uscPdpEcolabelTitle: "Information on the energy consumption and CO2 emissions of the car",
    uscPdpEcolabelFuelType: "Fuel",
    uscPdpEcolabelOtherFuelType: "Other energy source",
    uscPdpEcolabelNotApplicable: "not applicable",
    uscPdpEcolabelElectric: "Electric",
    uscPdpEcolabelHydrogen: "Hydrogen",
    uscPdpEcolabelEfficiencyClassTitle: "Efficiency Class",
    uscPdpEcolabelEfficiencyClassDescription: "",
    uscPdpEcolabelCombined: "Combined",
    uscPdpEcoLabelWeightedCombined: "Weighted combined",
    uscPdpEcolabelWithDischargedBattery: "with discharged battery",

    // TDG - Ecolabel - TMEF section
    uscPdpEcolabelYearPlaceholder: "{value}/year",
    uscPdpEcolabelTaxExempt: "Tax exempt",

    // Equipment
    uscPdpEquipmentTitle: "Equipment",

    // Packs + Accessories
    uscPdpAccessoriesContainerTitle: "Our accessories",

    uscPdpAccessoriesTitle: "Accessories",
    uscPdpAccessoriesSliderCount: "{count} available accessories",
    uscPdpAccessoriesSliderCountSingle: "{count} available accessory",
    uscPdpPacksTitle: "Packs",
    uscPdpPacksShowFeatures: "Show features",
    uscPdpPacksHideFeatures: "Hide features",
    uscPdpAccessoriesDisclaimer: "",

    // Service history
    uscPdpServiceTitle: "Service History",
    uscPdpServiceDescription:
        "This car is maintained accordingly to the highest factory standards. It has also been thoroughly tested, deep cleaned, and fully waxed before arriving in our showroom.",
    uscPdpServiceYearsCount: "{count} years of service",
    uscPdpServiceYearsCountSingle: "{count} year of service",
    uscPdpServiceChecksCount: "{count} checks",
    uscPdpServiceChecksCountSingle: "{count} check",
    uscPdpServiceOfficialDealer: "Official Toyota Dealer",
    uscPdpServiceHistoryTitle: "History",

    // Car Details
    uscPdpCarDetailsTitle: "Car Details",

    // Banner - Vehicle type
    uscPdpBannerTitle: "Customer Promise", // "Customer Promise" / "Benefits of cars in stock"

    // Banner - HUB cars -
    uscPdpBannerHubTitle: "Test drive this or a similar {model}",
    uscPdpBannerHubDescription:
        "This {model} is currently in stock. However, it is not located at the dealer’s showroom yet. Please contact the dealer to make an appointment or to book a test drive.",

    // Dealer
    uscPdpDealerDetailsTitle: "Contact details",
    uscPdpDealerOpeningHoursTitle: "Approved Sales",
    uscPdpLearnMoreAboutDealer: "", // "Learn more about this dealer"
    uscPdpDealerCarFilterLink: "See all cars from this dealer",
    uscPdpDealerContactLinkTitle: "Open dealer link",
    uscPdpDealerImpressumTitle: "Impressum",

    // Reserved / Sold
    uscPdpStatusPromotion: "Promotion",
    uscPdpStatusReserved: "Reserved",
    uscPdpStatusSold: "Sold",
    uscPdpStatusReservedTitle: "This {model} has been reserved",

    // Vehicle aggregation
    uscVehicleAggregationLess: "Less",
    uscVehicleAggregationMore: "More",
    uscVehicleAggregationCarsAvailable: "than 5 models available",

    // ----------------------------------------------------------------------
    // Basket
    // ----------------------------------------------------------------------

    // General
    uscBasketConfigRetailPrice: "Retail price",
    uscBasketInsurance: "Insurance",
    uscBasketInsuranceDescription: "Get a personalized insurance quote",
    uscBasketTitleAdditionalTaxesAndCosts: "Additional costs",
    uscBasketVehicleCodeTitle: "Car ID",

    // Finance tabs
    uscFinanceOptionCashOnly: "Cash payment",
    uscFinanceOptionSelectedCash: "Selected cash",
    uscFinanceOptionSelectedMonthly: "Selected monthly",
    uscFinanceOptionSwitchToCash: "Switch to cash",
    uscFinanceOptionSwitchToMonthly: "Switch to monthly",
    uscFinanceOptionCashToggle: "Cash",
    uscFinanceOptionMonthlyToggle: "Finance",

    // Services
    uscBasketServicesTitle: "Services",
    uscBasketServicesStandardWarrantyTitle: "Standard Warranty - {value}",
    uscBasketServicesCarDeliveryTitle: "Car Delivery",
    uscBasketServicesIncluded: "INCLUDED",

    // Overview
    uscBasketOverviewTotalCash: "Total to pay cash",
    uscBasketOverviewTotalMonthly: "Total to pay monthly",
    uscBasketOverviewFinanceOption: "", // Selected payment
    uscBasketOverviewFinanceOptionCash: "Cash",
    uscBasketOverviewFinanceOptionMonthly: "Monthly",

    // Ctas
    uscBasketCtaPrint: "Print",
    uscBasketCtaSave: "Save to MyToyota",
    uscBasketCtaLoading: "Loading",
    uscBasketCtaFinanceOptions: "Finance Options",
    uscBasketCtaBasketDetails: "Basket details",
    uscBasketCtaAddInsurance: "Add insurance",

    uscBasketCtaToFilter: "Back to results",
    uscBasketTotalPriceErrorDisclaimer: "", // Shown when TMEF calc fails.
    uscInsuranceCalculatorButtonAdd: "Add insurance quote",
    uscInsuranceCalculatorButtonEdit: "Edit insurance quote",

    // Reserved / Sold
    uscBasketStatusReserved: "This car has been reserved",
    uscBasketStatusSold: "This car has been sold",
    uscBasketStatusReservedDisclaimer: "Contact the dealer to find out if this or a similar car is still available.",
    uscBasketStatusSoldDisclaimer: "Contact the dealer to find out if this or a similar car is still available.",

    // Checkout
    uscBasketOpenReserveCheckoutCta: "Complete your purchase",
    uscBasketOpenBuyCheckoutCta: "Complete your purchase",

    // Previous / Next
    uscBasketPreviousCarCta: "Previous car",
    uscBasketNextCarCta: "Next car",

    // ----------------------------------------------------------------------
    // Services
    // ----------------------------------------------------------------------

    // General
    uscServicesExtrasTitle: "Our services",
    uscServicesPdpTitle: "Services",
    uscServicesPdpDescription: "You can configure your services on the next step.",

    // Services
    uscServicesWarranty: "Warranty",

    // Services - Types
    uscServicesOptional: "Optional",
    uscServicesIncluded: "Included",

    // Services - Warranty
    uscServicesWarrantyTitle: "Warranty",
    uscServicesWarrantyDescription: "", // optional
    uscServicesWarrantyFree: "", // optional
    uscServicesWarrantyShowMoreCta: "", // optional
    uscServicesWarrantyShowMoreModalTitle: "", // optional
    uscServicesWarrantyShowMoreModalBody: "", // optional

    // Services - Finance
    uscServicesFinanceTitle: "Finance",
    uscServicesFinanceEditDetails: "Edit details",
    uscServicesFinanceShowDetails: "Show details",

    // Services - Insurance
    uscServicesInsuranceTitle: "Insurance",
    uscServicesInsuranceEditDetails: "Edit details",
    uscServicesInsuranceShowDetails: "Show details",

    // ----------------------------------------------------------------------
    // Extras
    // ----------------------------------------------------------------------

    // General - Reserve
    uscExtrasReserveTitle: "Get the best for your car",

    // General - Buy
    uscExtrasBuyTitle: "Get the best for your car",
    uscExtrasBuyDescription: "",

    // Customer support - Reserve
    uscExtrasReserveSupportQuestion: "Got a question?",
    uscExtrasReserveSupportQuestionDescription: "",
    uscExtrasReserveSupportContact: "Contact us by phone",
    uscExtrasReserveSupportPhoneNr: "123456789",
    uscExtrasReserveSupportPhoneNrLabel: "Call 123456789",
    uscExtrasReserveSupportPhoneNrDescription: "",

    // Customer support - Buy
    uscExtrasBuySupportQuestion: "Got a question?",
    uscExtrasBuySupportQuestionDescription: "",
    uscExtrasBuySupportContact: "Contact us by phone",
    uscExtrasBuySupportPhoneNr: "123456789",
    uscExtrasBuySupportPhoneNrLabel: "Call 123456789",
    uscExtrasBuySupportPhoneNrDescription: "",

    // Checkout steps - General
    uscExtrasCheckoutInfo: "Read more about the reservation process",

    // Checkout steps - Reserve
    uscExtrasReserveStepsTitle: "Reserve your car now",
    uscExtrasReserveStepsDescription:
        "Found your ideal car? Make sure that you do not miss out on it and guarantee yourself the best price.",
    uscExtrasReserveSteps:
        "Log in or register on My Toyota;Confirm your reservation details;Pay a reservation fee;Reservation confirmed! Your Toyota dealer will get in touch with you.",

    // Checkout steps - Buy
    uscExtrasBuyStepsTitle: "Buy your car now",
    uscExtrasBuyStepsDescription:
        "Found your ideal car? Make sure that you do not miss out on it and guarantee yourself the best price.",
    uscExtrasBuySteps:
        "Log in or register on My Toyota;Confirm your reservation details;Pay a reservation fee;Reservation confirmed! Your Toyota dealer will get in touch with you.",

    // ----------------------------------------------------------------------
    // Car Delivery
    // ----------------------------------------------------------------------

    // Service block
    uscCarDeliveryServiceTitle: "Get your car delivered",
    uscCarDeliveryServiceAdded: "Added",
    uscCarDeliveryServiceButton: "Discover more",
    uscCarDeliveryServiceEditButton: "Edit delivery options",
    uscCarDeliveryServiceDescription:
        "Get the car you like from wherever it is, we bring it to you with different delivery options.",
    uscCarDeliveryServiceAddDeliveryButton: "Add delivery",
    uscCarDeliveryServiceDestinationDealer: "Dealer delivery",
    uscCarDeliveryServiceDestinationHome: "Home delivery",
    uscCarDeliveryServiceUnavailable: "Only available in cash",

    // More info modal
    uscCarDeliveryInfoTitle: "Car Delivery",
    uscCarDeliveryInfoCashOnly: "Cash only",
    uscCarDeliveryInfoHeader: "Get your car delivered to your doorstep in 4 easy steps",
    uscCarDeliveryInfoIntro: "Intro",
    uscCarDeliveryInfoBullets:
        "Select our car delivery service;Select a delivery option;Select type of transportation;Delivery confirmed! Your Toyota dealer will get in touch.",
    uscCarDeliveryInfoDescription: "Description",
    uscCarDeliveryInfoMoreInfo: "More info",
    uscCarDeliveryInfoMoreClose: "Close",

    // Configurator modal
    uscCarDeliveryBackButton: "Back",
    uscCarDeliveryConfirmButton: "Confirm",
    uscCarDeliveryStepTransportation: "Transportation",
    uscCarDeliveryDealerResultsError: "An error has occurred while retrieving the dealerships",
    uscCarDeliveryDealerResultsEmpty: "No dealerships found",
    uscCarDeliveryDealerResultDistance: "{value} km",
    uscCarDeliveryStepDestinationTitle: "Destination",
    uscCarDeliveryInputDealerPlaceholder: "City, ZIP code, or dealership",
    uscCarDeliveryInputHomePlaceholder: "City, ZIP code, or address",
    uscCarDeliveryNoDestinationSelected: "No destination selected",
    uscCarDeliverySelectDestinationTitle: "Select destination",
    uscCarDeliverySummaryDestinationTitle: "Destination",
    uscCarDeliverySummaryStartingPointTitle: "Starting point",
    uscCarDeliverySelectDestinationOptionHome: "Home delivery",
    uscCarDeliverySelectDestinationDescription: "Select a destination to get started",
    uscCarDeliverySelectDestinationOptionDealership: "Delivery at dealership",

    // Select Transportation
    uscCarDeliverySelectTransportationTitle: "Select transportation type",
    uscCarDeliverySelectTransportationDescription: "Select a transportation type to get started",
    uscCarDeliverySelectTransportationErrorTitle: "Something went wrong",
    uscCarDeliverySelectTransportationErrorMessage: "An error has occurred while retrieving the transportation options",
    uscCarDeliverySelectTransportationOptionTruckTitle: "Delivery by Truck",
    uscCarDeliverySelectTransportationOptionTruckDescription: "We bring your car in a dedicated truck",
    uscCarDeliverySelectTransportationOptionProfessionalTitle: "Delivery by Professional",
    uscCarDeliverySelectTransportationOptionProfessionalDescription:
        "Driven only by professionals. Fully insurance coverage for the road drive. You do not pay for benzine.",
    uscCarDeliverySelectTransportationOptionSelected: "Selected",

    // Notification
    uscCarDeliveryNotificationMonthlyTitle: "Car Delivery",
    uscCarDeliveryNotificationMonthlyDescription: "Car Delivery is not available for monthly payments",
};

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

export type UscLabelStateType = {
    commonSettings: CommonSettingsType;
    uscSettings: UscSettingsType;
};

export type UscLabelType = keyof typeof uscLabels;
export type UscLabelParamType = Partial<
    Record<
        | "model"
        | "count"
        | "value"
        | "address"
        | "city"
        | "zip"
        | "region"
        | "province"
        | "country"
        | "location"
        | "price"
        | "current"
        | "max"
        | "from"
        | "until",
        string | number
    >
>;

export const useUscLabel = getUseLabel(
    (state: UscStateType) => state.uscSettings.labels,
    (state: UscStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseLabelType<UscLabelType, UscLabelParamType>;

export const useUscOptionalLabel = getUseOptionalLabel(
    (state: UscStateType) => state.uscSettings.labels,
    (state: UscStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseOptionalLabelType<UscLabelType, UscLabelParamType>;

export const getUscLabel = getLabelFn(
    (state: UscLabelStateType) => state.uscSettings.labels,
    (state: UscLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetLabelType<UscLabelStateType, UscLabelType, UscLabelParamType>;

export const getUscOptionalLabel = getOptionalLabelFn(
    (state: UscLabelStateType) => state.uscSettings.labels,
    (state: UscLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetOptionalLabelType<UscLabelStateType, UscLabelType, UscLabelParamType>;

export const UscLabel = getLabelComponent(useUscLabel) as LabelComponentType<UscLabelType, UscLabelParamType>;
